"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { landingCopy } from "./copy";
import { motion, AnimatePresence } from "framer-motion";
import EnquiryForm from "./EnquiryForm";
import { useEnquiryForm } from "./useEnquiryForm";
import { LandingSectionBadge } from "./LandingSectionBadge";

const dashboardImages = [
  {
    src: "/images/Main Frame.svg",
    alt: "Dashboard Preview 1",
  },
  {
    src: "/images/Main Frame (1).svg",
    alt: "Dashboard Preview 2",
  },
];

export default function Hero() {
  const { isAuthenticated } = useAuth();
  const { isOpen, setIsOpen } = useEnquiryForm();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % dashboardImages.length);
    };
    const interval = setInterval(tick, 8000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % dashboardImages.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + dashboardImages.length) % dashboardImages.length);
  };

  const goToSlide = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.42, 0, 0.58, 1] as const, // easeOut cubic bezier
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 40 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.42, 0, 0.58, 1] as const, // easeOut cubic bezier
        delay: 0.4,
      },
    },
  };

  const slideTransition = {
    x: { type: "tween" as const, duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] as const },
    opacity: { duration: 0.28 },
    scale: { duration: 0.28 },
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 280 : -280,
      opacity: 0,
      scale: 0.98,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 280 : -280,
      opacity: 0,
      scale: 0.98,
    }),
  };

  const stats = [
    { value: "99.9%", label: "Uptime SLA" },
    { value: "8+", label: "Outreach channels" },
    { value: "ISO", label: "27001 aligned" },
  ];

  return (
    <section className="relative overflow-hidden border-b border-border/40 bg-gradient-to-b from-background via-primary/[0.03] to-background pt-28 pb-16 sm:pt-32 lg:pt-36 lg:pb-24">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -left-24 top-1/4 h-[min(420px,70vw)] w-[min(420px,70vw)] rounded-full bg-primary/12 blur-2xl" />
        <div className="absolute -right-32 bottom-0 h-[min(380px,65vw)] w-[min(380px,65vw)] rounded-full bg-cyan-500/10 blur-2xl dark:bg-cyan-400/10" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div
            className="text-center lg:text-left"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <LandingSectionBadge className="mb-6">Debt recovery automation</LandingSectionBadge>
            </motion.div>

            <motion.h1
              className="mb-6 text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-[3.25rem] xl:text-6xl"
              variants={itemVariants}
            >
              <span className="bg-gradient-to-r from-primary via-blue-600 to-cyan-600 bg-clip-text text-transparent">
                {landingCopy.hero.headline}
              </span>
            </motion.h1>

            <motion.p
              className="mx-auto mb-8 max-w-xl text-lg leading-relaxed text-muted-foreground lg:mx-0 lg:text-xl"
              variants={itemVariants}
            >
              {landingCopy.hero.subhead}
            </motion.p>

            <motion.div
              className="mb-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center lg:justify-start"
              variants={itemVariants}
            >
              {isAuthenticated ? (
                <Button
                  asChild
                  size="lg"
                  variant="primary"
                  className="h-auto px-8 py-6 text-base shadow-lg shadow-primary/25 transition-shadow hover:shadow-xl hover:shadow-primary/30"
                >
                  <Link href="/dashboard">
                    {landingCopy.cta.dashboard}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="primary"
                  className="h-auto px-8 py-6 text-base shadow-lg shadow-primary/25 transition-shadow hover:shadow-xl hover:shadow-primary/30"
                  onClick={() => setIsOpen(true)}
                >
                  {landingCopy.cta.primary}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              )}
              <Button asChild size="lg" variant="outline" className="h-auto border-2 px-8 py-6 text-base bg-background/60 backdrop-blur-sm">
                <Link href="/login">{landingCopy.cta.secondary}</Link>
              </Button>
            </motion.div>

            <motion.div
              className="mb-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:justify-start"
              variants={itemVariants}
            >
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl border border-border/60 bg-card/60 px-5 py-3 text-left shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-primary/25 hover:shadow-md"
                >
                  <p className="text-2xl font-bold tabular-nums text-foreground">{s.value}</p>
                  <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </motion.div>

            <motion.div
              className="flex items-center justify-center gap-2 text-sm text-muted-foreground lg:justify-start"
              variants={itemVariants}
            >
              <span>Already have an account?</span>
              <Link href="/login" className="font-semibold text-primary underline-offset-4 transition-colors hover:underline">
                Login here
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            className="relative min-w-0"
            variants={imageVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="relative mx-auto w-full max-w-xl xl:max-w-2xl">
              <div className="rounded-[1.75rem] bg-gradient-to-br from-primary/55 via-sky-500/35 to-cyan-500/45 p-px shadow-[0_28px_90px_-24px_oklch(0.55_0.18_245_/_0.4)]">
                <div className="relative overflow-hidden rounded-[1.7rem] border border-white/20 bg-gradient-to-br from-card/95 via-background/95 to-primary/[0.04] dark:border-white/10">
                {/* Carousel Images */}
                <div className="relative aspect-video w-full overflow-hidden">
                  <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                      key={currentIndex}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={slideTransition}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <div className="relative flex h-full w-full items-center justify-center p-2 sm:p-4 md:p-6">
                        <img
                          src={dashboardImages[currentIndex].src}
                          alt={dashboardImages[currentIndex].alt}
                          className="h-full w-full object-contain drop-shadow-2xl"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary/5 via-transparent to-transparent" />
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Navigation buttons */}
                  {dashboardImages.length > 1 && (
                    <>
                      <div className="absolute left-2 top-1/2 z-30 -translate-y-1/2 sm:left-4">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 rounded-full border-2 border-primary/30 bg-white/95 shadow-xl transition-transform hover:scale-105 active:scale-95 dark:bg-gray-800/95 sm:h-12 sm:w-12"
                          onClick={prevSlide}
                          aria-label="Previous slide"
                        >
                          <ChevronLeft className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
                        </Button>
                      </div>
                      <div className="absolute right-2 top-1/2 z-30 -translate-y-1/2 sm:right-4">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 rounded-full border-2 border-primary/30 bg-white/95 shadow-xl transition-transform hover:scale-105 active:scale-95 dark:bg-gray-800/95 sm:h-12 sm:w-12"
                          onClick={nextSlide}
                          aria-label="Next slide"
                        >
                          <ChevronRight className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Gradient fade edges */}
                  <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white/50 dark:from-gray-900/50 to-transparent pointer-events-none z-20" />
                  <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white/50 dark:from-gray-900/50 to-transparent pointer-events-none z-20" />
                </div>
                </div>
              </div>

              {dashboardImages.length > 1 && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  {dashboardImages.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => goToSlide(index)}
                      className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ease-out ${
                        index === currentIndex ? "w-8 bg-primary" : "w-2.5 bg-primary/35 hover:bg-primary/55"
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                      aria-current={index === currentIndex ? "true" : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
      <EnquiryForm open={isOpen} onOpenChange={setIsOpen} />
    </section>
  );
}

